'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronDownIcon, UsersRoundIcon } from 'lucide-react';

import { APP_GITHUB_REPO_URL } from '@workspace/common/app';
import { baseUrl, getPathname, routes } from '@workspace/routes';
import { GitHubIcon } from '@workspace/ui/components/brand-icons';
import { buttonVariants } from '@workspace/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@workspace/ui/components/dropdown-menu';
import { Logo } from '@workspace/ui/components/logo';
import { ThemeToggle } from '@workspace/ui/components/theme-toggle';
import { cn } from '@workspace/ui/lib/utils';

import { COMMUNITY_LINKS, MENU_LINKS } from '~/components/marketing-links';
import { MobileMenu } from '~/components/mobile-menu';
import { useMarketingAnalytics } from '~/hooks/use-marketing-analytics';

export function Navbar(): React.JSX.Element {
  const pathname = usePathname();
  const { trackDiscordClick, trackDocsClick, trackGithubClick } =
    useMarketingAnalytics();
  const handleCommunityClick = (name: string): void => {
    if (name === 'Discord') {
      trackDiscordClick('navbar_community_discord');
    }

    if (name === 'GitHub') {
      trackGithubClick('navbar_community_github');
    }
  };

  return (
    <section className="sticky inset-x-0 top-0 z-40 border-b bg-background/95 py-4 backdrop-blur">
      <div className="container">
        <nav className="hidden items-center justify-between lg:flex">
          <div className="flex items-center gap-8 xl:gap-10">
            <Link
              href={routes.marketing.Index}
              className="flex items-center gap-2"
            >
              <Logo />
            </Link>

            <div className="flex items-center gap-5 text-sm font-medium xl:gap-6">
              {MENU_LINKS.map((item) =>
                item.external ? (
                  <a
                    key={item.title}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {item.title}
                  </a>
                ) : (
                  <Link
                    key={item.title}
                    href={item.href}
                    onClick={() => {
                      if (item.title === 'Docs') {
                        trackDocsClick('navbar_docs', item.href);
                      }
                    }}
                    className={cn(
                      'transition-colors hover:text-foreground',
                      isMenuLinkActive(item.href, pathname)
                        ? 'text-foreground'
                        : 'text-muted-foreground'
                    )}
                  >
                    {item.title}
                  </Link>
                )
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 xl:gap-3">
            <ThemeToggle className="rounded-xl border-none shadow-none" />
            <Link
              href={routes.marketing.Contact}
              className={cn(buttonVariants({ variant: 'ghost' }), 'rounded-xl')}
            >
              Contact
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="group inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full border border-sky-500/20 bg-[linear-gradient(135deg,#0f172a,#0e7490)] px-3.5 text-[13px] font-semibold text-white shadow-[0_12px_34px_rgba(14,116,144,0.18)] transition hover:-translate-y-px hover:border-sky-400/40 hover:shadow-[0_16px_42px_rgba(14,116,144,0.25)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-500/50 dark:border-white/20 dark:bg-[linear-gradient(135deg,#ffffff,#dff6ff)] dark:text-slate-950 xl:h-10 xl:gap-2 xl:px-4 xl:text-sm"
                >
                  <UsersRoundIcon className="size-3.5 xl:size-4" />
                  Join our Community
                  <ChevronDownIcon className="size-3.5 transition-transform group-data-[state=open]:rotate-180 xl:size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={10}
                className="w-80 rounded-2xl border-border/70 bg-background/96 p-2 shadow-2xl backdrop-blur-xl"
              >
                <DropdownMenuLabel className="px-3 py-2">
                  <span className="block text-sm font-semibold">
                    Join the Viventium loop
                  </span>
                  <span className="mt-1 block text-xs font-normal leading-5 text-muted-foreground">
                    Follow the build, talk with the community, and inspect the
                    project.
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="mx-1" />
                {COMMUNITY_LINKS.map((item) =>
                  item.href ? (
                    <DropdownMenuItem
                      key={item.name}
                      asChild
                      className="cursor-pointer rounded-xl p-0"
                    >
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleCommunityClick(item.name)}
                        className="flex w-full items-center gap-3 px-3 py-2.5"
                      >
                        <span className="flex size-8 items-center justify-center rounded-full bg-muted text-foreground">
                          {item.icon}
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold">
                            {item.name}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground">
                            {item.handle}
                          </span>
                        </span>
                      </a>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem
                      key={item.name}
                      disabled
                      className="rounded-xl px-3 py-2.5 opacity-70"
                    >
                      <span className="flex size-8 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        {item.icon}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold">
                          {item.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {item.handle}
                        </span>
                      </span>
                    </DropdownMenuItem>
                  )
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <a
              href={APP_GITHUB_REPO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackGithubClick('navbar_github')}
              className={cn(
                buttonVariants({ variant: 'default' }),
                'rounded-full gap-2'
              )}
            >
              <GitHubIcon className="size-4" />
              GitHub
            </a>
          </div>
        </nav>

        <MobileMenu className="lg:hidden" />
      </div>
    </section>
  );
}

function isMenuLinkActive(href: string, pathname: string): boolean {
  const targetPath = getPathname(href, baseUrl.Marketing);

  if (href.includes('#features')) {
    return pathname === getPathname(routes.marketing.Index, baseUrl.Marketing);
  }

  if (targetPath === getPathname(routes.marketing.Index, baseUrl.Marketing)) {
    return pathname === targetPath;
  }

  return pathname === targetPath || pathname.startsWith(`${targetPath}/`);
}
